import { getVapidPublicKey, subscribePush } from "../api";

/** 현재 브라우저가 Web Push를 지원하는지 확인 */
export function isPushSupported(): boolean {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/**
 * VAPID base64url 키 → Uint8Array 변환 (표준 구현)
 * padding 보정, - → +, _ → / 치환 후 atob
 * ArrayBuffer로 명시 캐스팅하여 TS strict Uint8Array<ArrayBufferLike> 타입 충돌 방지
 */
export function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const base64Fixed = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64Fixed);
  const buffer = new ArrayBuffer(rawData.length);
  const output = new Uint8Array(buffer);
  for (let i = 0; i < rawData.length; i++) {
    output[i] = rawData.charCodeAt(i);
  }
  return output;
}

/** 현재 브라우저의 기존 Push 구독을 반환. 미지원/없으면 null */
export async function getPushSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

export type EnablePushResult =
  | { ok: true }
  | { ok: false; reason: "unsupported" | "denied" | "no-vapid" | "server" };

/**
 * Web Push 구독 활성화 전체 플로우.
 * 1. 미지원 체크 → "unsupported"
 * 2. Notification 권한 요청 → !granted면 "denied"
 * 3. VAPID 공개키 조회 → null이면 "no-vapid"
 * 4. pushManager.subscribe (기존 구독 있으면 재사용)
 * 5. 서버 저장 → 실패 시 "server"
 */
export async function enablePush(watchlist: string[]): Promise<EnablePushResult> {
  if (!isPushSupported()) return { ok: false, reason: "unsupported" };

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return { ok: false, reason: "denied" };

  const key = await getVapidPublicKey();
  if (!key) return { ok: false, reason: "no-vapid" };

  try {
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key),
      });
    }
    const saved = await subscribePush(sub.toJSON(), watchlist);
    if (!saved) return { ok: false, reason: "server" };
    return { ok: true };
  } catch {
    return { ok: false, reason: "server" };
  }
}

/** Web Push 구독 해제 */
export async function disablePush(): Promise<void> {
  const sub = await getPushSubscription();
  if (sub) {
    await sub.unsubscribe();
  }
}

/**
 * 이미 구독된 경우에만 서버 워치리스트를 갱신.
 * 구독 없으면 아무것도 하지 않음(중복 구독 방지).
 */
export async function syncPushWatchlist(watchlist: string[]): Promise<void> {
  const sub = await getPushSubscription();
  if (!sub) return;
  await subscribePush(sub.toJSON(), watchlist);
}
